import React from "react";
import { AlertCircle } from "lucide-react";
import ReusableDropdown, {
  unifiedControlStyle,
} from "../../../../../components/ReusableDropdown";
import DatePicker from "../../../../../components/DatePicker";
import AttendeesSection from "./AttendeesSection";
import RSVPSection from "./RSVPSection";
import EventFormActions from "./EventFormActions";

const EventForm = ({
  formData,
  setFormData,
  selectedEvent,
  isOriginator,
  isEditing,
  eventTypes,
  employeeList,
  suggestionFilter,
  setSuggestionFilter,
  setShowSuggestions,
  addEmployeeFromSuggestion,
  getCurrentUserRSVPStatus,
  errorMsg,
  t,
  language,
  theme,
  styles,
  screenSize,
  getShortName,
  onSubmit,
  onClose,
  onDelete,
  onStatusUpdate,
  handleButtonHover,
  handleInputChange,
}) => {
  const eventType = formData.type;

  const getEventTypeConfig = () => {
    switch (eventType) {
      case "vacation":
        return {
          showTime: false,
          showDuration: false,
          showEndDate: true,
          endDateRequired: true,
          showLocation: false,
          showAttendees: false,
          showRecurrence: false,
          showReminder: true,
          defaultDuration: 480,
          timeLabel: t.startTime,
          endDateLabel: t.endDate,
          endDateDescription: t.lastDayVacation,
        };
      case "deadline":
        return {
          showTime: true,
          showDuration: false,
          showEndDate: false,
          endDateRequired: false,
          showLocation: false,
          showAttendees: true,
          showRecurrence: false,
          showReminder: true,
          defaultDuration: 0,
          timeLabel: t.dueTime,
          endDateLabel: t.endDate,
          endDateDescription: t.extendedDeadline,
        };
      case "meeting":
      case "training":
      case "interview":
      case "review":
      case "conference":
        return {
          showTime: true,
          showDuration: true,
          showEndDate: false,
          endDateRequired: false,
          showLocation: true,
          showAttendees: true,
          showRecurrence: true,
          showReminder: true,
          defaultDuration: 60,
          timeLabel: t.startTime,
          endDateLabel: t.endDate,
          endDateDescription: t.multiDayEnd,
        };
      case "social":
        return {
          showTime: true,
          showDuration: true,
          showEndDate: false,
          endDateRequired: false,
          showLocation: true,
          showAttendees: true,
          showRecurrence: false,
          showReminder: true,
          defaultDuration: 120,
          timeLabel: t.startTime,
          endDateLabel: t.endDate,
          endDateDescription: t.multiDayEnd,
        };
      default:
        return {
          showTime: true,
          showDuration: true,
          showEndDate: false,
          endDateRequired: false,
          showLocation: true,
          showAttendees: true,
          showRecurrence: true,
          showReminder: true,
          defaultDuration: 60,
          timeLabel: t.startTime,
          endDateLabel: t.endDate,
          endDateDescription: t.multiDayEnd,
        };
    }
  };

  const eventConfig = getEventTypeConfig();

  const handleEventTypeChange = (e) => {
    const newType = e.target.value;
    setFormData((prev) => ({
      ...prev,
      type: newType,
      endDate: eventConfig.showEndDate ? prev.endDate : prev.date,
    }));
  };

  const dateRangeInvalid =
    formData.endDate && formData.date && formData.endDate < formData.date;

  const disabledStyle = !isOriginator && isEditing
    ? theme === "dark"
      ? "rgba(255,255,255,0.06)"
      : "rgba(255,255,255,0.5)"
    : undefined;

  return (
    <form onSubmit={onSubmit} style={styles.formContainer}>
      <RSVPSection
        isOriginator={isOriginator}
        isEditing={isEditing}
        formData={formData}
        selectedEvent={selectedEvent}
        getCurrentUserRSVPStatus={getCurrentUserRSVPStatus}
        getShortName={getShortName}
        t={t}
        language={language}
        theme={theme}
        styles={styles}
        screenSize={screenSize}
      />

      {/* Event Title */}
      <div
        style={
          styles.formGroup || {
            marginBottom: screenSize.isMobile ? "16px" : "20px",
          }
        }
      >
        <label style={styles.formLabel}>{t.eventTitle}</label>
        <input
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          style={{
            ...styles.formInput,
            ...unifiedControlStyle(theme, language === "ar"),
            background: disabledStyle || styles.formInput.background,
          }}
          placeholder={`${t.enterEventTitle} ${
            eventTypes
              .find((type) => type.id === eventType)
              ?.name?.toLowerCase() || (language === "ar" ? "حدث" : "event")
          } ${t.title}`}
          disabled={!isOriginator && isEditing}
          required
        />
      </div>

      {/* Description */}
      <div
        style={
          styles.formGroup || {
            marginBottom: screenSize.isMobile ? "16px" : "20px",
          }
        }
      >
        <label style={styles.formLabel}>{t.description}</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          style={{
            ...styles.formTextarea,
            ...unifiedControlStyle(theme, language === "ar"),
            minHeight: screenSize.isMobile ? 100 : 120,
            background: disabledStyle || styles.formTextarea.background,
          }}
          placeholder={
            eventType === "vacation"
              ? t.addVacationNotes
              : eventType === "deadline"
              ? t.addProjectDetails
              : t.addDetailedDescription
          }
          disabled={!isOriginator && isEditing}
        />
      </div>

      {/* Date/Time Grid */}
      <div style={styles.formGrid}>
        {/* Start Date */}
        <div>
          <label style={styles.formLabel}>
            {eventType === "vacation" ? t.startDate : t.date}
          </label>
          <DatePicker
            theme={theme}
            isRTL={language === "ar"}
            value={formData.date}
            onChange={(newStart) => {
              handleInputChange({ target: { name: "date", value: newStart } });
              if (
                (!formData.endDate || formData.endDate < newStart) &&
                eventConfig.showEndDate
              ) {
                setFormData((prev) => ({ ...prev, endDate: newStart }));
              }
            }}
            style={disabledStyle ? { opacity: 0.6, pointerEvents: "none" } : undefined}
          />
        </div>

        {/* End Date */}
        {eventConfig.showEndDate && (
          <div>
            <label style={styles.formLabel}>
              {eventConfig.endDateLabel}
              {eventConfig.endDateRequired && " *"}
            </label>
            <DatePicker
              theme={theme}
              isRTL={language === "ar"}
              value={formData.endDate || formData.date}
              onChange={(newEnd) =>
                handleInputChange({ target: { name: "endDate", value: newEnd } })
              }
              style={disabledStyle ? { opacity: 0.6, pointerEvents: "none" } : undefined}
            />
            {eventConfig.endDateDescription && (
              <div
                style={{
                  fontSize: screenSize.isMobile ? 11 : 12,
                  color:
                    theme === "dark"
                      ? "rgba(255,255,255,0.5)"
                      : "rgba(0,0,0,0.5)",
                  marginTop: 6,
                  textAlign: language === "ar" ? "right" : "left",
                }}
              >
                {eventConfig.endDateDescription}
              </div>
            )}
          </div>
        )}

        {/* Time */}
        {eventConfig.showTime && (
          <div>
            <label style={styles.formLabel}>{eventConfig.timeLabel}</label>
            <input
              name="time"
              type="time"
              value={formData.time}
              onChange={handleInputChange}
              style={{
                ...styles.formInput,
                ...unifiedControlStyle(theme, language === "ar"),
                background: disabledStyle || styles.formInput.background,
              }}
              disabled={!isOriginator && isEditing}
              required={eventConfig.showTime}
            />
          </div>
        )}

        {/* Duration */}
        {eventConfig.showDuration && (
          <div>
            <label style={styles.formLabel}>{t.duration}</label>
            <div style={styles.formInputWrapper}>
              <ReusableDropdown
                items={[
                  { id: 15, name: `15 ${t.minutes}` },
                  { id: 30, name: `30 ${t.minutes}` },
                  { id: 45, name: `45 ${t.minutes}` },
                  { id: 60, name: `1 ${t.hour}` },
                  { id: 90, name: `1.5 ${t.hours}` },
                  { id: 120, name: `2 ${t.hours}` },
                  { id: 180, name: `3 ${t.hours}` },
                  { id: 240, name: `4 ${t.hours}` },
                  { id: 480, name: t.allDay },
                ]}
                value={formData.duration}
                onSelect={(item) => {
                  const value =
                    typeof item === "string"
                      ? item
                      : item?.id || String(item);
                  handleInputChange({
                    target: { name: "duration", value },
                  });
                }}
                getItemKey={(it) => String(it.id)}
                getItemLabel={(it) => it.name}
                placeholder={t.duration}
                theme={theme}
                language={language}
                maxItems={10}
                disabled={!isOriginator && isEditing}
                inputStyle={{
                  ...unifiedControlStyle(theme, language === "ar"),
                  padding: screenSize.isMobile ? "12px 16px" : "16px 20px",
                  borderRadius: screenSize.isMobile ? "10px" : "14px",
                  width: "100%",
                  boxSizing: "border-box",
                  border:
                    theme === "dark"
                      ? "2px solid rgba(255,255,255,0.12)"
                      : "2px solid rgba(0,0,0,0.08)",
                  background:
                    theme === "dark"
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(255,255,255,0.9)",
                }}
              />
            </div>
          </div>
        )}

        {/* Priority */}
        <div>
          <label style={styles.formLabel}>{t.priority}</label>
          <div style={styles.formInputWrapper}>
            <ReusableDropdown
              items={[
                { id: "low", name: t.lowPriority },
                { id: "medium", name: t.mediumPriority },
                { id: "high", name: t.highPriority },
                { id: "urgent", name: t.urgent },
              ]}
              value={formData.priority}
              onSelect={(item) => {
                const value =
                  typeof item === "string" ? item : item?.id || String(item);
                handleInputChange({ target: { name: "priority", value } });
              }}
              getItemKey={(it) => it.id}
              getItemLabel={(it) => it.name}
              placeholder={t.priority}
              theme={theme}
              language={language}
              maxItems={6}
              disabled={!isOriginator && isEditing}
              inputStyle={{
                ...unifiedControlStyle(theme, language === "ar"),
                padding: screenSize.isMobile ? "12px 16px" : "16px 20px",
                borderRadius: screenSize.isMobile ? "10px" : "14px",
                width: "100%",
                boxSizing: "border-box",
                border:
                  theme === "dark"
                    ? "2px solid rgba(255,255,255,0.12)"
                    : "2px solid rgba(0,0,0,0.08)",
                background:
                  theme === "dark"
                    ? "rgba(255,255,255,0.06)"
                    : "rgba(255,255,255,0.9)",
              }}
            />
          </div>
        </div>

        {/* Status */}
        <div
          style={
            styles.formGroup || {
              marginBottom: screenSize.isMobile ? "16px" : "20px",
            }
          }
        >
          <label style={styles.formLabel}>
            {!isOriginator && isEditing ? t.yourStatus : t.status}
          </label>
          <div style={styles.formInputWrapper}>
            <ReusableDropdown
              items={
                !isOriginator && isEditing
                  ? [
                      { id: "accepted", name: t.accepted },
                      { id: "declined", name: t.declined },
                      { id: "tentative", name: t.tentative },
                      { id: "no-response", name: t.noResponse },
                    ]
                  : [
                      { id: "scheduled", name: t.scheduled },
                      { id: "confirmed", name: t.confirmed },
                      { id: "in-progress", name: t.inProgress },
                      { id: "completed", name: t.completed },
                      { id: "cancelled", name: t.cancelled },
                      { id: "postponed", name: t.postponed },
                    ]
              }
              value={formData.status}
              onSelect={(item) => {
                const value =
                  typeof item === "string" ? item : item?.id || String(item);
                handleInputChange({ target: { name: "status", value } });
              }}
              getItemKey={(it) => it.id}
              getItemLabel={(it) => it.name}
              placeholder={!isOriginator && isEditing ? t.yourStatus : t.status}
              theme={theme}
              language={language}
              maxItems={8}
              inputStyle={{
                ...unifiedControlStyle(theme, language === "ar"),
                padding: screenSize.isMobile ? "12px 16px" : "16px 20px",
                borderRadius: screenSize.isMobile ? "10px" : "14px",
                width: "100%",
                boxSizing: "border-box",
                border:
                  theme === "dark"
                    ? "2px solid rgba(255,255,255,0.12)"
                    : "2px solid rgba(0,0,0,0.08)",
                background:
                  theme === "dark"
                    ? "rgba(255,255,255,0.06)"
                    : "rgba(255,255,255,0.9)",
              }}
            />
          </div>
        </div>

        {/* Event Type */}
        <div
          style={
            styles.formGroup || {
              marginBottom: screenSize.isMobile ? "16px" : "20px",
            }
          }
        >
          <div>
            <label style={styles.formLabel}>{t.eventType}</label>
            <div style={styles.formInputWrapper}>
              <ReusableDropdown
                items={eventTypes.filter((it) => it.id !== "all")}
                value={formData.type}
                onSelect={(item) => {
                  const value =
                    typeof item === "string"
                      ? item
                      : item?.id || String(item);
                  handleEventTypeChange({ target: { name: "type", value } });
                }}
                getItemKey={(it) => it.id}
                getItemLabel={(it) => it.name}
                placeholder={t.eventType}
                theme={theme}
                language={language}
                maxItems={8}
                disabled={!isOriginator && isEditing}
                inputStyle={{
                  ...unifiedControlStyle(theme, language === "ar"),
                  padding: screenSize.isMobile ? "12px 16px" : "16px 20px",
                  borderRadius: screenSize.isMobile ? "10px" : "14px",
                  width: "100%",
                  boxSizing: "border-box",
                  border:
                    theme === "dark"
                      ? "2px solid rgba(255,255,255,0.12)"
                      : "2px solid rgba(0,0,0,0.08)",
                  background:
                    theme === "dark"
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(255,255,255,0.9)",
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Date range validation warning */}
      {dateRangeInvalid && (
        <div
          style={{
            color: "#ef4444",
            fontSize: screenSize.isMobile ? 12 : 14,
            fontWeight: 600,
            marginTop: 8,
            padding: screenSize.isMobile ? "8px 12px" : "12px 16px",
            background: "rgba(239, 68, 68, 0.06)",
            borderRadius: screenSize.isMobile ? 8 : 12,
            border: "1px solid rgba(239, 68, 68, 0.12)",
            textAlign: language === "ar" ? "right" : "left",
          }}
        >
          {t.endDateEarlier}
        </div>
      )}

      {/* Location */}
      {eventConfig.showLocation && (
        <div
          style={
            styles.formGroup || {
              marginBottom: screenSize.isMobile ? "16px" : "20px",
            }
          }
        >
          <label style={styles.formLabel}>
            {eventType === "conference"
              ? t.venue
              : eventType === "meeting"
              ? t.meetingLocation
              : t.location}
          </label>
          <input
            name="location"
            value={formData.location}
            onChange={handleInputChange}
            style={{
              ...styles.formInput,
              ...unifiedControlStyle(theme, language === "ar"),
              background: disabledStyle || styles.formInput.background,
            }}
            placeholder={
              eventType === "conference"
                ? t.conferenceVenue
                : eventType === "meeting"
                ? t.meetingRoom
                : t.locationDetails
            }
            disabled={!isOriginator && isEditing}
          />
        </div>
      )}

      {/* Attendees */}
      {eventConfig.showAttendees && (isOriginator || isEditing) && (
        <AttendeesSection
          isOriginator={isOriginator}
          isEditing={isEditing}
          formData={formData}
          setFormData={setFormData}
          employeeList={employeeList}
          suggestionFilter={suggestionFilter}
          setSuggestionFilter={setSuggestionFilter}
          setShowSuggestions={setShowSuggestions}
          addEmployeeFromSuggestion={addEmployeeFromSuggestion}
          t={t}
          language={language}
          theme={theme}
          styles={styles}
          screenSize={screenSize}
        />
      )}

      {/* Error Message */}
      {errorMsg && (
        <div
          style={{
            color: "#ef4444",
            fontSize: screenSize.isMobile ? 12 : 14,
            fontWeight: 600,
            marginTop: 16,
            padding: screenSize.isMobile ? "8px 12px" : "12px 16px",
            background: "rgba(239, 68, 68, 0.06)",
            borderRadius: screenSize.isMobile ? 8 : 12,
            border: "1px solid rgba(239, 68, 68, 0.12)",
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexDirection: language === "ar" ? "row-reverse" : "row",
            textAlign: language === "ar" ? "right" : "left",
          }}
        >
          <AlertCircle size={16} />
          {errorMsg}
        </div>
      )}

      <EventFormActions
        isOriginator={isOriginator}
        isEditing={isEditing}
        selectedEvent={selectedEvent}
        getCurrentUserRSVPStatus={getCurrentUserRSVPStatus}
        onDelete={onDelete}
        onClose={onClose}
        onStatusUpdate={onStatusUpdate}
        t={t}
        styles={styles}
        handleButtonHover={handleButtonHover}
      />
    </form>
  );
};

export default EventForm;
